// Concern-Ingredient Mapping for K-Beauty Recommendations
// Based on K-Cosmetic Product Analysis: 3 main ingredient categories

// ============================================================================
// PerfectCorp Metric → DB Concern Field Mapping
// ============================================================================

export type PerfectCorpMetric =
  | 'hd_wrinkle'
  | 'hd_pore'
  | 'hd_texture'
  | 'hd_acne'
  | 'hd_oiliness'
  | 'hd_radiance'
  | 'hd_moisture'
  | 'hd_redness'
  | 'hd_firmness'
  | 'hd_age_spot'
  | 'hd_dark_circle'
  | 'hd_eye_bag'

// Constants for DB concern fields - use these instead of string literals
export const DB_CONCERN = {
  Hydration: 'Hydration',
  CleansingPower: 'Cleansing_Power',
  Whitening: 'Whitening',
  AntiWrinkle: 'Anti_Wrinkle',
  PoreCare: 'Pore_Care',
  AcneCare: 'Acne_Care',
  Soothing: 'Soothing',
  BarrierStrengthening: 'Barrier_Strengthening',
  RednessSensitivity: 'Redness_Sensitivity',
  Pigmentation: 'Pigmentation',
  Firmness: 'Firmness',
  EyeCare: 'Eye_Care',
  ToneDullness: 'Tone_Dullness',
  OilControl: 'Oil_Control',
  TextureSmoothing: 'Texture_Smoothing',
  BlackheadCare: 'Blackhead_Care',
} as const

export type DBConcernField = (typeof DB_CONCERN)[keyof typeof DB_CONCERN]

// Maps PerfectCorp analysis metrics to product DB concern fields
export const PERFECTCORP_TO_DB_CONCERN: Record<PerfectCorpMetric, DBConcernField[]> = {
  hd_wrinkle: [DB_CONCERN.AntiWrinkle],
  hd_pore: [DB_CONCERN.PoreCare, DB_CONCERN.BlackheadCare],
  hd_texture: [DB_CONCERN.TextureSmoothing],
  hd_acne: [DB_CONCERN.AcneCare],
  hd_oiliness: [DB_CONCERN.OilControl],
  hd_radiance: [DB_CONCERN.ToneDullness, DB_CONCERN.Whitening],
  hd_moisture: [DB_CONCERN.Hydration, DB_CONCERN.BarrierStrengthening],
  hd_redness: [DB_CONCERN.RednessSensitivity, DB_CONCERN.Soothing],
  hd_firmness: [DB_CONCERN.Firmness],
  hd_age_spot: [DB_CONCERN.Pigmentation, DB_CONCERN.Whitening],
  hd_dark_circle: [DB_CONCERN.EyeCare],
  hd_eye_bag: [DB_CONCERN.EyeCare],
}

// ============================================================================
// 3 Ingredient Categories (from K-Cosmetic Product Analysis)
// ============================================================================

export type IngredientCategory = 'regeneration' | 'pigment_trouble' | 'barrier_soothing'

export interface IngredientInfo {
  name: string
  koreanName: string
  category: IngredientCategory
  benefits: string[]
  bestFor: DBConcernField[]
  avoidIf?: string[] // e.g., 'pregnancy', 'sensitive'
}

// Category 1: 재생·탄력·안티에이징 (Regeneration/Elasticity/Anti-aging)
// Best for: wrinkles, firmness, aging concerns
const REGENERATION_INGREDIENTS: IngredientInfo[] = [
  {
    name: 'PDRN',
    koreanName: '폴리뉴클레오타이드',
    category: 'regeneration',
    benefits: ['Cell regeneration', 'Wound healing', 'Anti-aging'],
    bestFor: [DB_CONCERN.AntiWrinkle, DB_CONCERN.Firmness, DB_CONCERN.TextureSmoothing],
  },
  {
    name: 'Peptide',
    koreanName: '펩타이드',
    category: 'regeneration',
    benefits: ['Collagen production', 'Firming', 'Anti-wrinkle'],
    bestFor: [DB_CONCERN.AntiWrinkle, DB_CONCERN.Firmness],
  },
  {
    name: 'Retinol',
    koreanName: '레티놀',
    category: 'regeneration',
    benefits: ['Cell turnover', 'Anti-aging', 'Texture improvement'],
    bestFor: [DB_CONCERN.AntiWrinkle, DB_CONCERN.TextureSmoothing, DB_CONCERN.PoreCare],
    avoidIf: ['pregnancy', 'sensitive'],
  },
  {
    name: 'Exosome',
    koreanName: '엑소좀',
    category: 'regeneration',
    benefits: ['Deep regeneration', 'Skin communication', 'Healing'],
    bestFor: [DB_CONCERN.AntiWrinkle, DB_CONCERN.Firmness, DB_CONCERN.BarrierStrengthening],
  },
  {
    name: 'Adenosine',
    koreanName: '아데노신',
    category: 'regeneration',
    benefits: ['Anti-wrinkle', 'Energy boost', 'Wound healing'],
    bestFor: [DB_CONCERN.AntiWrinkle, DB_CONCERN.Firmness],
  },
  {
    name: 'EGF',
    koreanName: '표피성장인자',
    category: 'regeneration',
    benefits: ['Cell growth', 'Regeneration', 'Wound healing'],
    bestFor: [DB_CONCERN.AntiWrinkle, DB_CONCERN.TextureSmoothing],
  },
]

// Category 2: 색소·트러블·톤 (Pigment/Trouble/Tone)
// Best for: dark spots, acne scars, dullness, pores
const PIGMENT_TROUBLE_INGREDIENTS: IngredientInfo[] = [
  {
    name: 'Vitamin C',
    koreanName: '비타민C',
    category: 'pigment_trouble',
    benefits: ['Brightening', 'Antioxidant', 'Collagen synthesis'],
    bestFor: [DB_CONCERN.Whitening, DB_CONCERN.Pigmentation, DB_CONCERN.ToneDullness],
  },
  {
    name: 'Niacinamide',
    koreanName: '나이아신아마이드',
    category: 'pigment_trouble',
    benefits: ['Brightening', 'Pore minimizing', 'Oil control'],
    bestFor: [DB_CONCERN.Whitening, DB_CONCERN.PoreCare, DB_CONCERN.OilControl, DB_CONCERN.Pigmentation],
  },
  {
    name: 'Tranexamic Acid',
    koreanName: '트라넥삼산',
    category: 'pigment_trouble',
    benefits: ['Brightening', 'Melasma treatment', 'Even tone'],
    bestFor: [DB_CONCERN.Pigmentation, DB_CONCERN.Whitening],
  },
  {
    name: 'Snail Mucin',
    koreanName: '달팽이점액',
    category: 'pigment_trouble',
    benefits: ['Hydration', 'Scar healing', 'Repair'],
    bestFor: [DB_CONCERN.Hydration, DB_CONCERN.AcneCare, DB_CONCERN.TextureSmoothing],
  },
  {
    name: 'Arbutin',
    koreanName: '알부틴',
    category: 'pigment_trouble',
    benefits: ['Brightening', 'Melanin inhibition', 'Even tone'],
    bestFor: [DB_CONCERN.Whitening, DB_CONCERN.Pigmentation],
  },
  {
    name: 'Salicylic Acid (BHA)',
    koreanName: '살리실산',
    category: 'pigment_trouble',
    benefits: ['Exfoliation', 'Pore cleansing', 'Acne treatment'],
    bestFor: [DB_CONCERN.AcneCare, DB_CONCERN.PoreCare, DB_CONCERN.BlackheadCare],
    avoidIf: ['pregnancy'],
  },
  {
    name: 'Glycolic Acid (AHA)',
    koreanName: '글리콜산',
    category: 'pigment_trouble',
    benefits: ['Exfoliation', 'Brightening', 'Texture improvement'],
    bestFor: [DB_CONCERN.TextureSmoothing, DB_CONCERN.ToneDullness, DB_CONCERN.Whitening],
    avoidIf: ['sensitive'],
  },
]

// Category 3: 장벽·진정·수분 (Barrier/Soothing/Hydration)
// Best for: sensitivity, redness, dryness, barrier damage
const BARRIER_SOOTHING_INGREDIENTS: IngredientInfo[] = [
  {
    name: 'Ceramide',
    koreanName: '세라마이드',
    category: 'barrier_soothing',
    benefits: ['Barrier repair', 'Moisture lock', 'Protection'],
    bestFor: [DB_CONCERN.BarrierStrengthening, DB_CONCERN.Hydration],
  },
  {
    name: 'Centella Asiatica (Cica)',
    koreanName: '센텔라/시카',
    category: 'barrier_soothing',
    benefits: ['Soothing', 'Healing', 'Anti-inflammatory'],
    bestFor: [DB_CONCERN.Soothing, DB_CONCERN.RednessSensitivity, DB_CONCERN.AcneCare],
  },
  {
    name: 'Panthenol',
    koreanName: '판테놀',
    category: 'barrier_soothing',
    benefits: ['Hydration', 'Healing', 'Soothing'],
    bestFor: [DB_CONCERN.Hydration, DB_CONCERN.Soothing, DB_CONCERN.BarrierStrengthening],
  },
  {
    name: 'Hyaluronic Acid',
    koreanName: '히알루론산',
    category: 'barrier_soothing',
    benefits: ['Deep hydration', 'Plumping', 'Moisture retention'],
    bestFor: [DB_CONCERN.Hydration],
  },
  {
    name: 'Aloe Vera',
    koreanName: '알로에',
    category: 'barrier_soothing',
    benefits: ['Soothing', 'Cooling', 'Hydration'],
    bestFor: [DB_CONCERN.Soothing, DB_CONCERN.RednessSensitivity, DB_CONCERN.Hydration],
  },
  {
    name: 'Madecassoside',
    koreanName: '마데카소사이드',
    category: 'barrier_soothing',
    benefits: ['Healing', 'Anti-inflammatory', 'Collagen boost'],
    bestFor: [DB_CONCERN.Soothing, DB_CONCERN.RednessSensitivity, DB_CONCERN.BarrierStrengthening],
  },
  {
    name: 'Beta-Glucan',
    koreanName: '베타글루칸',
    category: 'barrier_soothing',
    benefits: ['Soothing', 'Hydration', 'Immune support'],
    bestFor: [DB_CONCERN.Soothing, DB_CONCERN.Hydration, DB_CONCERN.RednessSensitivity],
  },
  {
    name: 'Propolis',
    koreanName: '프로폴리스',
    category: 'barrier_soothing',
    benefits: ['Antibacterial', 'Nourishing', 'Healing'],
    bestFor: [DB_CONCERN.AcneCare, DB_CONCERN.BarrierStrengthening, DB_CONCERN.Soothing],
  },
]

// Combined ingredients list
export const ALL_INGREDIENTS: IngredientInfo[] = [
  ...REGENERATION_INGREDIENTS,
  ...PIGMENT_TROUBLE_INGREDIENTS,
  ...BARRIER_SOOTHING_INGREDIENTS,
]

// ============================================================================
// Concern → Best Ingredients Mapping
// ============================================================================

export function getRecommendedIngredients(
  concerns: DBConcernField[],
  options?: {
    isPregnant?: boolean
    isSensitive?: boolean
    limit?: number
  }
): IngredientInfo[] {
  const { isPregnant = false, isSensitive = false, limit = 5 } = options || {}

  // Score each ingredient by how many concerns it addresses
  const ingredientScores = ALL_INGREDIENTS.map((ingredient) => {
    // Skip if should be avoided
    if (isPregnant && ingredient.avoidIf?.includes('pregnancy')) {
      return { ingredient, score: -1 }
    }
    if (isSensitive && ingredient.avoidIf?.includes('sensitive')) {
      return { ingredient, score: -1 }
    }

    // Count matching concerns
    const matchCount = concerns.filter((concern) => ingredient.bestFor.includes(concern)).length

    return { ingredient, score: matchCount }
  })

  // Filter out excluded ingredients and sort by score
  return ingredientScores
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((item) => item.ingredient)
}

// ============================================================================
// Survey Condition → DB Concern Mapping
// ============================================================================

import type { SkinCondition } from '@/types/fullSurvey'

export const SURVEY_CONDITION_TO_CONCERN: Record<SkinCondition, DBConcernField[]> = {
  acne: [DB_CONCERN.AcneCare, DB_CONCERN.PoreCare, DB_CONCERN.OilControl],
  dryness: [DB_CONCERN.Hydration, DB_CONCERN.BarrierStrengthening],
  oiliness: [DB_CONCERN.OilControl, DB_CONCERN.PoreCare],
  sensitivity: [DB_CONCERN.Soothing, DB_CONCERN.RednessSensitivity, DB_CONCERN.BarrierStrengthening],
  hyperpigmentation: [DB_CONCERN.Pigmentation, DB_CONCERN.Whitening, DB_CONCERN.ToneDullness],
  rosacea: [DB_CONCERN.RednessSensitivity, DB_CONCERN.Soothing],
  eczema: [DB_CONCERN.BarrierStrengthening, DB_CONCERN.Soothing, DB_CONCERN.Hydration],
  none: [],
}

// ============================================================================
// Survey Include Ingredients → DB-friendly format
// ============================================================================

import type { IncludeIngredient } from '@/types/fullSurvey'

export const SURVEY_INGREDIENT_TO_NAME: Record<IncludeIngredient, string> = {
  niacinamide: 'Niacinamide',
  retinol: 'Retinol',
  'vitamin-c': 'Vitamin C',
  'hyaluronic-acid': 'Hyaluronic Acid',
  'salicylic-acid': 'Salicylic Acid (BHA)',
  'glycolic-acid': 'Glycolic Acid (AHA)',
  peptides: 'Peptide',
  centella: 'Centella Asiatica (Cica)',
  'snail-mucin': 'Snail Mucin',
  ceramides: 'Ceramide',
  'green-tea': 'Green Tea Extract',
  propolis: 'Propolis',
}

// ============================================================================
// Pregnancy-Unsafe Ingredients
// ============================================================================

export const PREGNANCY_UNSAFE_INGREDIENTS = [
  'Retinol',
  'Retinoids',
  'Tretinoin',
  'Adapalene',
  'Salicylic Acid',
  'Benzoyl Peroxide',
  'Hydroquinone',
  'Chemical Sunscreens (Oxybenzone)',
]

// ============================================================================
// Category Info for UI Display
// ============================================================================

export const INGREDIENT_CATEGORIES = {
  regeneration: {
    id: 'regeneration' as const,
    name: 'Regeneration & Anti-Aging',
    koreanName: '재생·탄력·안티에이징',
    description: 'Targets wrinkles, firmness, and signs of aging',
    color: '#E8D4F0', // Light purple
  },
  pigment_trouble: {
    id: 'pigment_trouble' as const,
    name: 'Pigment & Trouble Care',
    koreanName: '색소·트러블·톤',
    description: 'Targets dark spots, acne scars, dullness, and pores',
    color: '#FDE8D0', // Light orange
  },
  barrier_soothing: {
    id: 'barrier_soothing' as const,
    name: 'Barrier & Soothing',
    koreanName: '장벽·진정·수분',
    description: 'Targets sensitivity, redness, dryness, and barrier damage',
    color: '#D4E8F0', // Light blue
  },
}
